import { createCsvBlob, repairCsvText } from './csvEncoding'

const formatCurrency = (n) =>
  new Intl.NumberFormat('sk-SK', {
    style: 'currency',
    currency: 'EUR'
  }).format(Number(n || 0))

const toNumber = (value) => {
  const n = Number(value)
  return Number.isFinite(n) ? n : 0
}

const formatAmountCsv = (value) => toNumber(value).toFixed(2).replace('.', ',')

const formatAmountDisplay = (value) => toNumber(value).toFixed(2).replace('.', ',')

const formatAmountXls = (value) => toNumber(value).toFixed(2)

const escapeCsv = (v) => {
  const s = repairCsvText(v)

  if (s.includes('"') || s.includes(';') || s.includes('\n')) {
    return `"${s.replace(/"/g, '""')}"`
  }

  return s
}

const escapeXml = (v) =>
  String(v ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;')

const fileStamp = () => Date.now()

const downloadBlob = (blob, name) => {
  const a = document.createElement('a')
  a.href = URL.createObjectURL(blob)
  a.download = name
  a.click()
  URL.revokeObjectURL(a.href)
}

const buildHeaders = (t) => [
  t('common.date'),
  t('common.type', { defaultValue: 'Typ' }),
  t('common.note', { defaultValue: 'Poznámka' }),
  t('common.amount', { defaultValue: 'Suma' }),
  t('common.currency', { defaultValue: 'Mena' })
]

const toCSV = (rows, walletBalance, t) => {
  const balanceLine = `${t('wallet.balance')};${escapeCsv(formatCurrency(walletBalance))}`
  const header = buildHeaders(t).join(';')
  const body = rows
    .map((r) =>
      [r.date, r.type, r.note, formatAmountCsv(r.amount), r.currency]
        .map(escapeCsv)
        .join(';')
    )
    .join('\n')

  return `${balanceLine}\n\n${header}\n${body}`
}

const toXLSXml = (rows, walletBalance, t) => {
  const title = escapeXml(t('wallet.movements'))
  const balanceLabel = escapeXml(t('wallet.balance'))
  const headerCells = buildHeaders(t)
    .map((h) => `<Cell ss:StyleID="Header"><Data ss:Type="String">${escapeXml(h)}</Data></Cell>`)
    .join('')
  const dataRows = rows
    .map(
      (r) => `<Row>
        <Cell><Data ss:Type="String">${escapeXml(r.date)}</Data></Cell>
        <Cell><Data ss:Type="String">${escapeXml(r.type)}</Data></Cell>
        <Cell><Data ss:Type="String">${escapeXml(r.note)}</Data></Cell>
        <Cell ss:StyleID="Amount"><Data ss:Type="Number">${formatAmountXls(r.amount)}</Data></Cell>
        <Cell><Data ss:Type="String">${escapeXml(r.currency)}</Data></Cell>
      </Row>`
    )
    .join('')

  return `<?xml version="1.0"?>
<?mso-application progid="Excel.Sheet"?>
<Workbook
  xmlns="urn:schemas-microsoft-com:office:spreadsheet"
  xmlns:o="urn:schemas-microsoft-com:office:office"
  xmlns:x="urn:schemas-microsoft-com:office:excel"
  xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet"
  xmlns:html="http://www.w3.org/TR/REC-html40">
  <Styles>
    <Style ss:ID="Header"><Font ss:Bold="1"/><Interior ss:Color="#F0F0F0" ss:Pattern="Solid"/><Alignment ss:Horizontal="Center"/></Style>
    <Style ss:ID="Title"><Font ss:Bold="1" ss:Size="14"/></Style>
    <Style ss:ID="Meta"><Font ss:Bold="1"/></Style>
    <Style ss:ID="Amount"><NumberFormat ss:Format="0.00"/><Alignment ss:Horizontal="Right"/></Style>
  </Styles>
  <Worksheet ss:Name="Wallet">
    <Table>
      <Column ss:AutoFitWidth="0" ss:Width="130"/><Column ss:AutoFitWidth="0" ss:Width="130"/><Column ss:AutoFitWidth="0" ss:Width="280"/><Column ss:AutoFitWidth="0" ss:Width="90"/><Column ss:AutoFitWidth="0" ss:Width="70"/>
      <Row><Cell ss:StyleID="Title"><Data ss:Type="String">${title}</Data></Cell></Row>
      <Row/>
      <Row><Cell ss:StyleID="Meta"><Data ss:Type="String">${balanceLabel}</Data></Cell><Cell><Data ss:Type="String">${escapeXml(formatCurrency(walletBalance))}</Data></Cell></Row>
      <Row/>
      <Row>${headerCells}</Row>
      ${dataRows}
    </Table>
  </Worksheet>
</Workbook>`
}

export const exportWalletCSV = (rows, walletBalance, t) => {
  try {
        downloadBlob(createCsvBlob(toCSV(rows, walletBalance, t)), `wallet_movements_${fileStamp()}.csv`)
  } catch {
    window.alert(`${t('export.csvErrorTitle')}\n${t('export.csvError')}`)
  }
}

export const exportWalletXLS = (rows, walletBalance, t) => {
  try {
    downloadBlob(new Blob([toXLSXml(rows, walletBalance, t)], { type: 'application/vnd.ms-excel;charset=utf-8;' }), `wallet_movements_${fileStamp()}.xls`)
  } catch {
    window.alert(`${t('export.xlsErrorTitle')}\n${t('export.xlsError')}`)
  }
}

export const exportWalletPDF = async (rows, walletBalance, t) => {
  try {
    const { jsPDF } = await import('jspdf')
    const autoTable = (await import('jspdf-autotable')).default
    const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' })

    doc.setFontSize(18)
    doc.text(t('wallet.movements'), 40, 42)
    doc.setFontSize(12)
    doc.text(`${t('wallet.balance')}: ${formatCurrency(walletBalance)}`, 40, 64)
    autoTable(doc, {
      startY: 84,
      head: [buildHeaders(t)],
      body: rows.map((r) => [r.date, r.type, r.note, formatAmountDisplay(r.amount), r.currency]),
      styles: { fontSize: 9 },
      headStyles: { fillColor: [242, 242, 242], textColor: 20 },
      columnStyles: { 3: { halign: 'right' } }
    })
    downloadBlob(doc.output('blob'), `wallet_movements_${fileStamp()}.pdf`)
  } catch {
    window.alert(`${t('export.pdfErrorTitle')}\n${t('export.pdfError')}`)
  }
}
